import Head from 'next/head'

import type { FC, ReactNode } from 'react'

import { SITE } from '@/lib/site'

interface MetaProps {
    title?: string
    absoluteTitle?: string
    description?: string
    /** Deprecated: the meta keywords tag is no longer rendered (ignored by Google, flagged in the SEO guidelines). */
    keywords?: string[]
    canonical?: string
    ogTitle?: string
    ogDescription?: string
    ogImage?: string
    ogImageAlt?: string
    noindex?: boolean
    /** With noindex: still let crawlers follow the page's links ('noindex, follow'). */
    follow?: boolean
    /** 'article' on blog posts, which also emits article:published_time / modified_time. */
    ogType?: 'website' | 'article'
    publishedTime?: string | null
    modifiedTime?: string | null
}

const toAbsolute = (path: string): string => {
    try {
        return new URL(path, SITE.url).toString()
    } catch {
        return path
    }
}

export const Meta: FC<MetaProps> = ({
    title,
    absoluteTitle,
    description,
    canonical,
    ogTitle,
    ogDescription,
    ogImage = '/og.jpg',
    ogImageAlt,
    noindex = false,
    follow = false,
    ogType = 'website',
    publishedTime,
    modifiedTime
}): ReactNode => {
    const pageTitle =
        absoluteTitle ?? (title ? `${title} | ${SITE.brand}` : SITE.brand)
    const canonicalUrl = canonical ? toAbsolute(canonical) : undefined
    const imageUrl = toAbsolute(ogImage)
    const resolvedOgTitle = ogTitle ?? pageTitle
    const resolvedOgDescription = ogDescription ?? description

    return (
        <Head>
            <title>{pageTitle}</title>

            {description && <meta name='description' content={description} />}
            {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}

            {noindex ? (
                <meta name='robots' content={follow ? 'noindex, follow' : 'noindex, nofollow'} />
            ) : (
                <meta
                    name='robots'
                    content='index, follow, max-image-preview:large, max-snippet:-1'
                />
            )}

            <meta property='og:type' content={ogType} />
            {ogType === 'article' && publishedTime && (
                <meta property='article:published_time' content={publishedTime} />
            )}
            {ogType === 'article' && modifiedTime && (
                <meta property='article:modified_time' content={modifiedTime} />
            )}
            <meta property='og:site_name' content={SITE.brand} />
            <meta property='og:locale' content='en_GB' />
            {canonicalUrl && <meta property='og:url' content={canonicalUrl} />}
            <meta property='og:title' content={resolvedOgTitle} />
            {resolvedOgDescription && (
                <meta property='og:description' content={resolvedOgDescription} />
            )}
            <meta property='og:image' content={imageUrl} />
            {ogImageAlt && <meta property='og:image:alt' content={ogImageAlt} />}

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={resolvedOgTitle} />
            {resolvedOgDescription && (
                <meta name='twitter:description' content={resolvedOgDescription} />
            )}
            <meta name='twitter:image' content={imageUrl} />
        </Head>
    )
}
